'use client';

import { useCallback } from 'react';
import type { ApolloError } from '@apollo/client';
import { useActiveProjectId } from '@frameio/shared/contexts';
import { getLink } from '@frameio/shared/routing/getLink';
import { useTrack } from '@frameio/shared/segment/hooks';
import { SHARED_TEXT } from '../constants/sharedText';
import { useCreateRemoveBackgroundOperationMutation } from '../gql/mutations/generated/CreateRemoveBackgroundOperation';
import TEXT from './generated/TEXT/useRemoveBackgroundAction';
import { useFireflyErrorToast } from './useFireflyErrorToast';
import { useFireflyRateLimit } from './useFireflyRateLimit';
import { useVariationsProcessingToast } from './useVariationsProcessingToast';

TEXT.DEFINE({
  /** Name of the remove background action. */
  actionName: 'Remove Background',
});

const MAX_ASSETS_FOR_REMOVE_BACKGROUND = 100;

type RemoveBackgroundArgs = {
  assetIds: string[];
  trim?: boolean;
};

export function useFireflyRemoveBackgroundAction() {
  const [createRemoveBackground] = useCreateRemoveBackgroundOperationMutation();
  const projectId = useActiveProjectId();
  const showVariationsProcessingToast = useVariationsProcessingToast();
  const showErrorToast = useFireflyErrorToast();
  const { handleRateLimitError, rateLimitDialog } = useFireflyRateLimit();
  const track = useTrack();

  const tooManySelectedMessage = SHARED_TEXT.tooManySelectedText({
    actionName: TEXT.actionName,
    assetCount: MAX_ASSETS_FOR_REMOVE_BACKGROUND,
  });
  const startErrorMessage = SHARED_TEXT.errorMessageToast({
    actionName: TEXT.actionName,
  });

  const removeBackground = useCallback(
    async ({
      assetIds,
      trim = false,
    }: RemoveBackgroundArgs): Promise<boolean> => {
      if (!assetIds?.length) return false;
      if (assetIds.length > MAX_ASSETS_FOR_REMOVE_BACKGROUND) {
        showErrorToast(tooManySelectedMessage);
        return false;
      }
      try {
        const { data, errors } = await createRemoveBackground({
          variables: {
            input: {
              sourceAssetIds: assetIds,
              trim,
            },
          },
        });
        if (errors?.length) {
          if (!handleRateLimitError(errors)) {
            showErrorToast(startErrorMessage);
          }
          return false;
        }
        track('generation-started', {
          action_name: 'Remove Background',
          number_of_assets: assetIds.length,
          parameters_selected: 0,
        });
        const destinationId =
          data?.createRemoveBackgroundOperation?.fireflyOperation?.destination
            ?.id;
        const href = projectId
          ? getLink({ page: 'folder', projectId, folderAssetId: destinationId })
          : null;
        if (href) showVariationsProcessingToast({ href, projectId });
        return true;
      } catch (error) {
        if (!handleRateLimitError(error as ApolloError)) {
          showErrorToast(startErrorMessage);
        }
        return false;
      }
    },
    [
      createRemoveBackground,
      projectId,
      showErrorToast,
      handleRateLimitError,
      showVariationsProcessingToast,
      track,
      tooManySelectedMessage,
      startErrorMessage,
    ]
  );
  return { removeBackground, rateLimitDialog };
}
